import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import MainLayout from '../../components/ui/MainLayout';
import Icon from '../../components/AppIcon';
import { useLanguage } from '../../contexts/LanguageContext';

import { useRealtime } from '../../contexts/RealtimeContext';
import { getStayOccupancyStatus, getTodayStr } from '../../utils/occupancyStatus';

const BUILDING_COLORS = {
  F1: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500', bar: '#3B82F6', barLight: '#DBEAFE' },
  F2: { bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-200', dot: 'bg-violet-500', bar: '#8B5CF6', barLight: '#EDE9FE' },
  G1: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500', bar: '#F59E0B', barLight: '#FEF3C7' },
  G2: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500', bar: '#F97316', barLight: '#FFEDD5' },
  H:  { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-200', dot: 'bg-rose-500', bar: '#EF4444', barLight: '#FEE2E2' },
  L:  { bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-200', dot: 'bg-teal-500', bar: '#14B8A6', barLight: '#CCFBF1' },
};

const STATUS_COLORS = {
  'confirmed-stay':     { bg: '#10B981', gradient: 'linear-gradient(135deg, #10B981, #059669)', text: '#fff', label: 'Confirmed Stay',     labelFr: 'Séjour confirmé',    icon: 'CheckCircle' },
  'active-reservation': { bg: '#F59E0B', gradient: 'linear-gradient(135deg, #F59E0B, #D97706)', text: '#fff', label: 'Reserved',           labelFr: 'Réservation active', icon: 'Clock' },
  'completed-stay':     { bg: '#94A3B8', gradient: 'linear-gradient(135deg, #94A3B8, #64748B)', text: '#fff', label: 'Completed Stay',      labelFr: 'Séjour terminé',     icon: 'CheckSquare' },
  active:               { bg: '#10B981', gradient: 'linear-gradient(135deg, #10B981, #059669)', text: '#fff', label: 'Confirmed Stay',     labelFr: 'Séjour confirmé',    icon: 'CheckCircle' },
  pending:              { bg: '#F59E0B', gradient: 'linear-gradient(135deg, #F59E0B, #D97706)', text: '#fff', label: 'Reserved',           labelFr: 'Réservation active', icon: 'Clock' },
  'checked-out':        { bg: '#94A3B8', gradient: 'linear-gradient(135deg, #94A3B8, #64748B)', text: '#fff', label: 'Completed Stay',      labelFr: 'Séjour terminé',     icon: 'CheckSquare' },
};

// Deduplicated status entries for legend
const LEGEND_STATUSES = [
  { key: 'confirmed-stay',     ...STATUS_COLORS?.['confirmed-stay'] },
  { key: 'active-reservation', ...STATUS_COLORS?.['active-reservation'] },
  { key: 'completed-stay',     ...STATUS_COLORS?.['completed-stay'] },
];

const MONTH_NAMES_EN = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTH_NAMES_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const DAY_NAMES_EN = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const DAY_NAMES_FR = ['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'];

const COL_W = 32; // px per day column
const ROW_H = 44; // px per row
const LABEL_W = 160; // px for left label column

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0)?.getDate();
}
function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1)?.getDay();
}
function toDateStr(year, month, day) {
  return `${year}-${String(month + 1)?.padStart(2, '0')}-${String(day)?.padStart(2, '0')}`;
}
function parseDate(str) {
  if (!str) return null;
  const [y, m, d] = str?.split('-')?.map(Number);
  return new Date(y, m - 1, d);
}
function financialStatusLabel(val, language) {
  if (val === 'covered') return language === 'fr' ? 'Pris en charge par Senghor' : 'Covered by Senghor';
  if (val === 'not-covered') return language === 'fr' ? 'Non pris en charge par Senghor' : 'Not Covered by Senghor';
  return val || '—';
}
function formatDate(str, language) {
  if (!str) return '—';
  const d = parseDate(str);
  if (!d) return str;
  const months = language === 'fr' ? MONTH_NAMES_FR : MONTH_NAMES_EN;
  return `${months?.[d?.getMonth()]} ${d?.getDate()}, ${d?.getFullYear()}`;
}

// ── Guest Detail Drawer ────────────────────────────────────────────────────────
function GuestDetailDrawer({ stay, language, onClose }) {
  useEffect(() => {
    function handleKey(e) { if (e?.key === 'Escape') onClose(); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const isOpen = !!stay;
  const statusCfg = stay ? (STATUS_COLORS?.[stay?.status] ?? STATUS_COLORS?.pending) : null;
  const checkInDate = stay ? parseDate(stay?.checkIn) : null;
  const checkOutDate = stay ? parseDate(stay?.checkOut) : null;
  const lengthOfStay = checkInDate && checkOutDate
    ? Math.round((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24))
    : null;

  const today = new Date(); today?.setHours(0,0,0,0);
  const isUpcoming = checkInDate && checkInDate > today;
  const isActive = checkInDate && checkOutDate && checkInDate <= today && checkOutDate >= today;
  const isCompleted = checkOutDate && checkOutDate < today;

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/25 backdrop-blur-[2px] transition-opacity duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />
      <div
        className={`fixed top-0 right-0 h-full z-50 w-80 bg-card border-l border-border shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: statusCfg?.gradient || '#6B7280' }}>
              <Icon name="User" size={15} className="text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">{language === 'fr' ? 'Détails du séjour' : 'Stay Details'}</p>
              <p className="text-[11px] text-muted-foreground">{language === 'fr' ? 'Informations sur la réservation' : 'Reservation information'}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
            <Icon name="X" size={16} />
          </button>
        </div>

        {stay && (
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {/* Name + status */}
            <div>
              <h2 className="text-lg font-bold text-foreground">{stay?.residentName}</h2>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <div
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
                  style={{ background: statusCfg?.bg + '22', color: statusCfg?.bg, border: `1px solid ${statusCfg?.bg}44` }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusCfg?.bg }} />
                  {language === 'fr' ? statusCfg?.labelFr : statusCfg?.label}
                </div>
                {isUpcoming && (
                  <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-600 border border-blue-200">
                    <Icon name="CalendarClock" size={10} />
                    {language === 'fr' ? 'À venir' : 'Upcoming'}
                  </div>
                )}
                {isActive && (
                  <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    {language === 'fr' ? 'En cours' : 'Active now'}
                  </div>
                )}
                {isCompleted && (
                  <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-500 border border-slate-200">
                    <Icon name="CheckSquare" size={10} />
                    {language === 'fr' ? 'Terminé' : 'Completed'}
                  </div>
                )}
              </div>
            </div>

            {/* Guest info */}
            <div className="bg-muted/40 rounded-xl p-4 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{language === 'fr' ? 'Informations du résident' : 'Guest Information'}</p>
              <div className="space-y-2.5">
                <InfoRow icon="User" label={language === 'fr' ? 'Nom complet' : 'Full Name'} value={stay?.residentName} />
                {stay?.department && <InfoRow icon="Building2" label={language === 'fr' ? 'Département' : 'Department'} value={stay?.department} />}
                {stay?.financialStatus && (
                  <InfoRow
                    icon="CreditCard"
                    label={language === 'fr' ? 'Statut financier' : 'Financial Status'}
                    value={financialStatusLabel(stay?.financialStatus, language)}
                    valueClass={stay?.financialStatus === 'covered' ? 'text-emerald-600' : 'text-rose-600'}
                  />
                )}
              </div>
            </div>

            {/* Stay info */}
            <div className="bg-muted/40 rounded-xl p-4 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{language === 'fr' ? 'Informations du séjour' : 'Stay Information'}</p>
              <div className="space-y-2.5">
                <InfoRow icon="DoorOpen" label={language === 'fr' ? 'Chambre' : 'Room'} value={stay?.room} mono />
                {stay?.building && <InfoRow icon="Building" label={language === 'fr' ? 'Bâtiment' : 'Building'} value={stay?.building} />}
                <InfoRow icon="LogIn" label={language === 'fr' ? 'Arrivée' : 'Check-in'} value={formatDate(stay?.checkIn, language)} iconClass="text-emerald-600" />
                <InfoRow icon="LogOut" label={language === 'fr' ? 'Départ' : 'Check-out'} value={formatDate(stay?.checkOut, language)} iconClass="text-rose-500" />
                {lengthOfStay !== null && (
                  <InfoRow icon="Clock" label={language === 'fr' ? 'Durée du séjour' : 'Length of Stay'} value={`${lengthOfStay} ${language === 'fr' ? 'jours' : 'days'}`} />
                )}
              </div>
            </div>
          </div>
        )}

        <div className="px-5 py-4 border-t border-border flex-shrink-0">
          <button onClick={onClose} className="w-full py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
            {language === 'fr' ? 'Fermer' : 'Close'}
          </button>
        </div>
      </div>
    </>
  );
}

function InfoRow({ icon, label, value, mono, iconClass, valueClass }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-7 h-7 rounded-lg bg-card flex items-center justify-center flex-shrink-0">
        <Icon name={icon} size={13} className={iconClass || 'text-muted-foreground'} />
      </div>
      <div>
        <p className="text-[11px] text-muted-foreground">{label}</p>
        <p className={`text-sm font-semibold text-foreground ${mono ? 'font-mono' : ''} ${valueClass || ''}`}>{value || '—'}</p>
      </div>
    </div>
  );
}

// ── Hover Tooltip ──────────────────────────────────────────────────────────────
function StayTooltip({ stay, language, visible, x, y }) {
  if (!visible || !stay) return null;
  const statusCfg = STATUS_COLORS?.[stay?.status] ?? STATUS_COLORS?.pending;
  const checkInDate = parseDate(stay?.checkIn);
  const checkOutDate = parseDate(stay?.checkOut);
  const lengthOfStay = checkInDate && checkOutDate
    ? Math.round((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div
      className="fixed z-[9999] pointer-events-none"
      style={{ left: x + 12, top: y - 8 }}
    >
      <div className="bg-card border border-border rounded-xl shadow-2xl p-3 w-56 text-xs"
        style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 4px 16px rgba(0,0,0,0.1)' }}>
        {/* Status bar at top */}
        <div className="h-1 rounded-full mb-3" style={{ background: statusCfg?.gradient }} />
        <div className="font-bold text-foreground text-sm mb-0.5">{stay?.residentName}</div>
        <div
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold mb-2"
          style={{ background: statusCfg?.bg + '22', color: statusCfg?.bg }}
        >
          <span className="w-1 h-1 rounded-full" style={{ background: statusCfg?.bg }} />
          {language === 'fr' ? statusCfg?.labelFr : statusCfg?.label}
        </div>
        <div className="space-y-1 text-[11px]">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{language === 'fr' ? 'Chambre' : 'Room'}</span>
            <span className="font-semibold font-mono text-foreground">{stay?.room}</span>
          </div>
          {stay?.building && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{language === 'fr' ? 'Bâtiment' : 'Building'}</span>
              <span className="font-semibold text-foreground">{stay?.building}</span>
            </div>
          )}
          <div className="border-t border-border/60 my-1.5" />
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{language === 'fr' ? 'Arrivée' : 'Check-in'}</span>
            <span className="font-semibold text-emerald-600">{formatDate(stay?.checkIn, language)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{language === 'fr' ? 'Départ' : 'Check-out'}</span>
            <span className="font-semibold text-rose-500">{formatDate(stay?.checkOut, language)}</span>
          </div>
          {lengthOfStay !== null && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{language === 'fr' ? 'Durée' : 'Duration'}</span>
              <span className="font-semibold text-foreground">{lengthOfStay} {language === 'fr' ? 'j' : 'd'}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Reservation Bar (Timeline) ─────────────────────────────────────────────────
function ReservationBarTimeline({ stay, startDay, spanDays, daysInMonth, language, statusCfg, onSelect, today, currentYear, currentMonth }) {
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0 });

  // Pixel-accurate positioning using fixed column widths
  const leftPx = (startDay - 1) * COL_W;
  const widthPx = Math.max(spanDays * COL_W - 2, 4);

  const todayStr = getTodayStr();
  const checkInDate  = parseDate(stay?.checkIn);
  const checkOutDate = parseDate(stay?.checkOut);
  const todayMidnight = new Date(); todayMidnight?.setHours(0,0,0,0);

  // Use date-driven logic for bar appearance
  const occupancyStatus = getStayOccupancyStatus(stay, todayStr);
  const isUpcoming  = occupancyStatus === 'Reserved' && stay?.checkIn > todayStr;
  const isCompleted = checkOutDate && checkOutDate < todayMidnight;
  const isActive    = occupancyStatus === 'Occupied';

  function handleMouseMove(e) {
    setTooltip({ visible: true, x: e?.clientX, y: e?.clientY });
  }
  function handleMouseLeave() {
    setTooltip({ visible: false, x: 0, y: 0 });
  }

  const barStyle = {
    left: leftPx,
    width: widthPx,
    height: ROW_H - 12,
    background: isCompleted
      ? 'linear-gradient(135deg, #94A3B8, #64748B)'
      : isUpcoming
        ? 'linear-gradient(135deg, #60A5FA, #3B82F6)'
        : statusCfg?.gradient || statusCfg?.bg,
    borderRadius: 8,
    boxShadow: isCompleted
      ? 'none'
      : isActive
        ? `0 2px 8px ${statusCfg?.bg}55, 0 1px 3px rgba(0,0,0,0.1)`
        : `0 1px 4px rgba(0,0,0,0.08)`,
    opacity: isCompleted ? 0.7 : 1,
    cursor: 'pointer',
    position: 'absolute',
    top: 6,
    zIndex: 10,
    transition: 'all 0.15s ease',
    border: isActive ? `1px solid ${statusCfg?.bg}88` : '1px solid rgba(0,0,0,0.06)',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 8,
    paddingRight: 8,
    userSelect: 'none',
  };

  return (
    <>
      <StayTooltip stay={stay} language={language} visible={tooltip?.visible} x={tooltip?.x} y={tooltip?.y} />
      <div
        style={barStyle}
        onClick={e => { e?.stopPropagation(); onSelect?.(stay); }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="group"
      >
        {/* Shimmer overlay for active stays */}
        {isActive && (
          <div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)', borderRadius: 8 }}
          />
        )}
        {/* Left accent stripe */}
        <div
          className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg opacity-60"
          style={{ background: 'rgba(255,255,255,0.4)' }}
        />
        {spanDays >= 3 && (
          <span className="text-[10px] font-semibold text-white truncate leading-none whitespace-nowrap pl-1 drop-shadow-sm">
            {stay?.residentName?.split(' ')?.[0]} · {stay?.room}
          </span>
        )}
        {spanDays >= 6 && (
          <span className="text-[9px] text-white/70 ml-1.5 truncate leading-none whitespace-nowrap hidden sm:inline">
            {stay?.checkIn?.slice(5)} → {stay?.checkOut?.slice(5)}
          </span>
        )}
      </div>
    </>
  );
}

// ── Timeline View ──────────────────────────────────────────────────────────────
function TimelineView({ stays, currentYear, currentMonth, language, buildingFilter }) {
  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const monthStart = new Date(currentYear, currentMonth, 1);
  const monthEnd = new Date(currentYear, currentMonth, daysInMonth);

  const [guestSearch, setGuestSearch] = useState('');
  const [roomFilter, setRoomFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedStay, setSelectedStay] = useState(null);
  const [lastSync, setLastSync] = useState(new Date());
  const [isSyncing, setIsSyncing] = useState(false);
  const [boxHeight, setBoxHeight] = useState(380);
  const isDragging = useRef(false);
  const startY = useRef(0);
  const startHeight = useRef(0);
  const scrollRef = useRef(null);
  const headerRef = useRef(null);

  // Sync indicator
  useEffect(() => {
    const interval = setInterval(() => {
      setIsSyncing(true);
      setTimeout(() => { setLastSync(new Date()); setIsSyncing(false); }, 600);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Scroll sync between header and body
  useEffect(() => {
    const body = scrollRef?.current;
    const header = headerRef?.current;
    if (!body || !header) return;
    function onBodyScroll() { header.scrollLeft = body?.scrollLeft; }
    body?.addEventListener('scroll', onBodyScroll);
    return () => body?.removeEventListener('scroll', onBodyScroll);
  }, []);

  function formatLastSync(date) {
    const diffSec = Math.floor((new Date() - date) / 1000);
    if (diffSec < 10) return language === 'fr' ? 'À l\'instant' : 'Just now';
    if (diffSec < 60) return language === 'fr' ? `Il y a ${diffSec}s` : `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    return language === 'fr' ? `Il y a ${diffMin} min` : `${diffMin}m ago`;
  }

  const filteredStays = stays?.filter(s => {
    if (!s?.checkIn || !s?.checkOut) return false;
    if (buildingFilter !== 'all' && s?.building !== buildingFilter) return false;
    const ciDate = parseDate(s?.checkIn);
    const coDate = parseDate(s?.checkOut);
    if (!ciDate || !coDate) return false;
    if (ciDate > monthEnd || coDate < monthStart) return false;
    if (guestSearch?.trim()) {
      const q = guestSearch?.trim()?.toLowerCase();
      if (!s?.residentName?.toLowerCase()?.includes(q)) return false;
    }
    if (roomFilter?.trim()) {
      const r = roomFilter?.trim()?.toLowerCase();
      if (!s?.room?.toLowerCase()?.includes(r)) return false;
    }
    if (statusFilter !== 'all' && s?.status !== statusFilter) return false;
    return true;
  });

  const today = new Date();
  const todayDay = today?.getFullYear() === currentYear && today?.getMonth() === currentMonth ? today?.getDate() : null;

  // Resize handle
  function onResizeMouseDown(e) {
    e?.preventDefault();
    isDragging.current = true;
    startY.current = e?.clientY;
    startHeight.current = boxHeight;
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  }
  useEffect(() => {
    function onMouseMove(e) {
      if (!isDragging?.current) return;
      setBoxHeight(Math.max(160, startHeight?.current + (e?.clientY - startY?.current)));
    }
    function onMouseUp() {
      if (!isDragging?.current) return;
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => { document.removeEventListener('mousemove', onMouseMove); document.removeEventListener('mouseup', onMouseUp); };
  }, []);

  const statusOptions = [
    { value: 'all', labelEn: 'All', labelFr: 'Tous' },
    { value: 'confirmed-stay', labelEn: 'Confirmed', labelFr: 'Confirmé' },
    { value: 'active-reservation', labelEn: 'Reserved', labelFr: 'Réservé' },
    { value: 'completed-stay', labelEn: 'Completed', labelFr: 'Terminé' },
  ];

  const totalGridWidth = LABEL_W + daysInMonth * COL_W;

  return (
    <>
      <GuestDetailDrawer stay={selectedStay} language={language} onClose={() => setSelectedStay(null)} />
      <div className="bg-card border border-border rounded-2xl overflow-hidden flex flex-col shadow-sm" style={{ position: 'relative' }}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex-shrink-0 bg-card">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="BarChart2" size={14} className="text-primary" />
            </div>
            <h3 className="text-sm font-bold text-foreground">{language === 'fr' ? 'Vue chronologique des séjours' : 'Stay Timeline View'}</h3>
            <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-muted text-muted-foreground">
              {filteredStays?.length} {language === 'fr' ? 'séjour(s)' : 'stay(s)'}
            </span>
            <div className="ml-auto flex items-center gap-1.5 pl-3 border-l border-border">
              <div className={`w-2 h-2 rounded-full ${isSyncing ? 'bg-amber-400 animate-pulse' : 'bg-emerald-500 animate-pulse'}`} />
              <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                {isSyncing
                  ? (language === 'fr' ? 'Synchronisation…' : 'Syncing…')
                  : (language === 'fr' ? `Sync · ${formatLastSync(lastSync)}` : `Live · ${formatLastSync(lastSync)}`)}
              </span>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[140px] max-w-[200px]">
              <Icon name="Search" size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                value={guestSearch}
                onChange={e => setGuestSearch(e?.target?.value)}
                placeholder={language === 'fr' ? 'Nom du résident…' : 'Guest name…'}
                className="w-full pl-7 pr-3 py-1.5 text-xs bg-muted/50 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-foreground placeholder:text-muted-foreground"
              />
              {guestSearch && (
                <button onClick={() => setGuestSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <Icon name="X" size={11} />
                </button>
              )}
            </div>
            <div className="relative min-w-[100px] max-w-[140px]">
              <Icon name="DoorOpen" size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                value={roomFilter}
                onChange={e => setRoomFilter(e?.target?.value)}
                placeholder={language === 'fr' ? 'N° chambre…' : 'Room no…'}
                className="w-full pl-7 pr-3 py-1.5 text-xs bg-muted/50 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-foreground placeholder:text-muted-foreground"
              />
              {roomFilter && (
                <button onClick={() => setRoomFilter('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <Icon name="X" size={11} />
                </button>
              )}
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              {statusOptions?.map(opt => {
                const isActive = statusFilter === opt?.value;
                const sCfg = opt?.value !== 'all' ? STATUS_COLORS?.[opt?.value] : null;
                return (
                  <button
                    key={opt?.value}
                    onClick={() => setStatusFilter(opt?.value)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all flex items-center gap-1 ${
                      isActive ? 'border-transparent text-white' : 'bg-muted/50 border-border text-muted-foreground hover:text-foreground hover:border-primary/40'
                    }`}
                    style={isActive && sCfg ? { background: sCfg?.bg } : isActive ? { background: '#6B7280' } : {}}
                  >
                    {opt?.value !== 'all' && isActive && <span className="w-1.5 h-1.5 rounded-full bg-white/70 flex-shrink-0" />}
                    {language === 'fr' ? opt?.labelFr : opt?.labelEn}
                  </button>
                );
              })}
            </div>
            {(guestSearch || roomFilter || statusFilter !== 'all') && (
              <button
                onClick={() => { setGuestSearch(''); setRoomFilter(''); setStatusFilter('all'); }}
                className="flex items-center gap-1 px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground border border-border rounded-lg hover:border-primary/40 transition-all"
              >
                <Icon name="FilterX" size={11} />
                {language === 'fr' ? 'Réinitialiser' : 'Clear'}
              </button>
            )}
          </div>
        </div>

        {/* Sticky date header (synced scroll) */}
        <div
          ref={headerRef}
          className="overflow-hidden flex-shrink-0 border-b border-border bg-muted/20"
          style={{ overflowX: 'hidden' }}
        >
          <div style={{ minWidth: totalGridWidth, display: 'flex' }}>
            {/* Label column spacer */}
            <div className="flex-shrink-0 border-r border-border/60 bg-muted/30 flex items-center px-3" style={{ width: LABEL_W, height: 36 }}>
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">{language === 'fr' ? 'Résident · Chambre' : 'Resident · Room'}</span>
            </div>
            {/* Day columns */}
            {days?.map(d => {
              const isToday = d === todayDay;
              const dow = new Date(currentYear, currentMonth, d)?.getDay();
              const isWeekend = dow === 0 || dow === 6;
              return (
                <div
                  key={d}
                  className={`flex-shrink-0 flex flex-col items-center justify-center border-r border-border/40 ${
                    isToday ? 'bg-primary/15 text-primary' : isWeekend ? 'bg-muted/40 text-muted-foreground' : 'text-muted-foreground'
                  }`}
                  style={{ width: COL_W, height: 36 }}
                >
                  <span className={`text-[10px] font-bold leading-none ${isToday ? 'text-primary' : ''}`}>{d}</span>
                  {isToday && <span className="w-1 h-1 rounded-full bg-primary mt-0.5" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Scrollable body */}
        <div
          ref={scrollRef}
          className="overflow-auto flex-1"
          style={{ height: boxHeight }}
        >
          <div style={{ minWidth: totalGridWidth }}>
            {filteredStays?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-sm text-muted-foreground gap-3">
                <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center">
                  <Icon name="CalendarX2" size={22} className="text-muted-foreground/50" />
                </div>
                <span className="font-medium">{language === 'fr' ? 'Aucun séjour correspondant' : 'No matching stays'}</span>
                {(guestSearch || roomFilter || statusFilter !== 'all') && (
                  <button
                    onClick={() => { setGuestSearch(''); setRoomFilter(''); setStatusFilter('all'); }}
                    className="text-xs text-primary hover:underline"
                  >
                    {language === 'fr' ? 'Effacer les filtres' : 'Clear filters'}
                  </button>
                )}
              </div>
            ) : (
              filteredStays?.map((stay, idx) => {
                const ciDate = parseDate(stay?.checkIn);
                const coDate = parseDate(stay?.checkOut);
                const clampedStart = ciDate < monthStart ? monthStart : ciDate;
                const clampedEnd = coDate > monthEnd ? monthEnd : coDate;
                const startDay = clampedStart?.getDate();
                const endDay = clampedEnd?.getDate();
                const spanDays = endDay - startDay + 1;
                const statusCfg = STATUS_COLORS?.[stay?.status] ?? STATUS_COLORS?.pending;
                const buildingColor = BUILDING_COLORS?.[stay?.building];
                const isEven = idx % 2 === 0;

                return (
                  <div
                    key={stay?.id}
                    className="flex border-b border-border/30 hover:bg-primary/[0.02] transition-colors group"
                    style={{ height: ROW_H }}
                  >
                    {/* Label */}
                    <div
                      className="flex-shrink-0 flex items-center gap-2 px-3 border-r border-border/40 cursor-pointer hover:bg-muted/30 transition-colors"
                      style={{ width: LABEL_W }}
                      onClick={() => setSelectedStay(stay)}
                      title={language === 'fr' ? 'Voir les détails' : 'View details'}
                    >
                      <div
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ background: buildingColor?.bar ?? '#6B7280' }}
                      />
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-foreground truncate leading-tight">{stay?.residentName?.split(' ')?.[0]}</p>
                        <p className="text-[10px] text-muted-foreground font-mono truncate leading-tight">{stay?.room}</p>
                      </div>
                      <Icon name="ChevronRight" size={10} className="text-muted-foreground/40 group-hover:text-primary/60 transition-colors ml-auto flex-shrink-0" />
                    </div>
                    {/* Bar area */}
                    <div className="relative flex-1" style={{ height: ROW_H }}>
                      {/* Day grid columns */}
                      <div className="absolute inset-0 flex pointer-events-none">
                        {days?.map(d => {
                          const isToday = d === todayDay;
                          const dow = new Date(currentYear, currentMonth, d)?.getDay();
                          const isWeekend = dow === 0 || dow === 6;
                          return (
                            <div
                              key={d}
                              className={`flex-shrink-0 border-r border-border/20 ${
                                isToday ? 'bg-primary/8' : isWeekend ? 'bg-muted/20' : isEven ? 'bg-transparent' : 'bg-muted/5'
                              }`}
                              style={{ width: COL_W }}
                            />
                          );
                        })}
                      </div>

                      {/* Today indicator line */}
                      {todayDay && (
                        <div
                          className="absolute top-0 bottom-0 w-0.5 bg-primary/40 pointer-events-none z-20"
                          style={{ left: (todayDay - 1) * COL_W + COL_W / 2 }}
                        />
                      )}

                      {/* Reservation bar */}
                      <ReservationBarTimeline
                        stay={stay}
                        startDay={startDay}
                        spanDays={spanDays}
                        daysInMonth={daysInMonth}
                        language={language}
                        statusCfg={statusCfg}
                        onSelect={setSelectedStay}
                        today={today}
                        currentYear={currentYear}
                        currentMonth={currentMonth}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-5 py-3 border-t border-border bg-muted/10 flex-shrink-0">
          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mr-1">{language === 'fr' ? 'Légende' : 'Legend'}</span>
          {LEGEND_STATUSES?.map(cfg => (
            <div key={cfg?.key} className="flex items-center gap-1.5">
              <div className="w-4 h-3 rounded-sm flex-shrink-0" style={{ background: cfg?.gradient }} />
              <span className="text-[11px] text-muted-foreground">{language === 'fr' ? cfg?.labelFr : cfg?.label}</span>
            </div>
          ))}
          <div className="flex items-center gap-1.5 ml-2 pl-3 border-l border-border">
            <div className="w-3 h-3 rounded-sm bg-primary/15 border border-primary/30 flex-shrink-0" />
            <span className="text-[11px] text-muted-foreground">{language === 'fr' ? 'Aujourd\'hui' : 'Today'}</span>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${isSyncing ? 'bg-amber-400 animate-pulse' : 'bg-emerald-500 animate-pulse'}`} />
            <span className="text-[10px] text-muted-foreground">{isSyncing ? (language === 'fr' ? 'Synchronisation…' : 'Syncing…') : (language === 'fr' ? 'Données en direct' : 'Live data')}</span>
          </div>
        </div>

        {/* Resize handle */}
        <div
          onMouseDown={onResizeMouseDown}
          className="flex items-center justify-center h-4 cursor-ns-resize bg-muted/30 hover:bg-primary/10 border-t border-border/40 transition-colors group flex-shrink-0"
          title={language === 'fr' ? 'Glisser pour redimensionner' : 'Drag to resize'}
          style={{ borderBottomLeftRadius: '1rem', borderBottomRightRadius: '1rem' }}
        >
          <div className="flex gap-0.5 items-center">
            <div className="w-8 h-0.5 rounded-full bg-border group-hover:bg-primary/50 transition-colors" />
            <Icon name="GripHorizontal" size={12} className="text-muted-foreground group-hover:text-primary transition-colors mx-1" />
            <div className="w-8 h-0.5 rounded-full bg-border group-hover:bg-primary/50 transition-colors" />
          </div>
        </div>
      </div>
    </>
  );
}

// ── Main Calendar View ─────────────────────────────────────────────────────────
export default function CalendarView() {
  const { language } = useLanguage();
  const realtime = useRealtime();

  // Normalize Supabase snake_case fields to camelCase used by this component
  const stays = React.useMemo(() => {
    return (realtime?.stays || [])?.map(s => ({
      ...s,
      residentName: s?.resident_name || s?.residentName || '',
      room: s?.room_number || s?.room || '',
      checkIn: s?.check_in_date || s?.checkIn || '',
      checkOut: s?.check_out_date || s?.checkOut || '',
      building: s?.building || '',
      status: s?.status || '',
      guestStatus: s?.guest_status || s?.guestStatus || '',
      accommodationType: s?.accommodation_type || s?.accommodationType || '',
      financialStatus: s?.financial_status || s?.financialStatus || '',
      department: s?.department || '',
    }));
  }, [realtime?.stays]);

  const MONTH_NAMES = language === 'fr' ? MONTH_NAMES_FR : MONTH_NAMES_EN;
  const DAY_NAMES = language === 'fr' ? DAY_NAMES_FR : DAY_NAMES_EN;
  const now = new Date();
  const [currentYear, setCurrentYear] = useState(now?.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(now?.getMonth());
  const [selectedDay, setSelectedDay] = useState(null);
  const [buildingFilter, setBuildingFilter] = useState('all');
  const [activeView, setActiveView] = useState('timeline');

  const allStays = stays?.filter(s => s?.checkIn && s?.checkOut);
  const filteredStays = buildingFilter === 'all' ? allStays : allStays?.filter(s => s?.building === buildingFilter);

  function getEventsForDay(dateStr) {
    const checkIns = filteredStays?.filter(s => s?.checkIn === dateStr);
    const checkOuts = filteredStays?.filter(s => s?.checkOut === dateStr);
    const active = filteredStays?.filter(s => s?.checkIn < dateStr && s?.checkOut > dateStr);
    return { checkIns, checkOuts, active };
  }

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);

  function prevMonth() {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
    setSelectedDay(null);
  }
  function nextMonth() {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
    setSelectedDay(null);
  }

  const todayStr = toDateStr(now?.getFullYear(), now?.getMonth(), now?.getDate());
  const selectedDateStr = selectedDay ? toDateStr(currentYear, currentMonth, selectedDay) : null;
  const selectedEvents = selectedDateStr ? getEventsForDay(selectedDateStr) : null;

  const monthPrefix = `${currentYear}-${String(currentMonth + 1)?.padStart(2, '0')}`;
  const monthCheckIns = filteredStays?.filter(s => s?.checkIn?.startsWith(monthPrefix));
  const monthCheckOuts = filteredStays?.filter(s => s?.checkOut?.startsWith(monthPrefix));
  const activeStays = filteredStays?.filter(s => s?.status === 'active' || s?.status === 'confirmed-stay' || s?.status === 'pending');

  function getStaysForDay(dateStr) {
    return filteredStays?.filter(s => s?.checkIn <= dateStr && s?.checkOut >= dateStr);
  }

  return (
    <MainLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{language === 'fr' ? 'Vue calendrier' : 'Calendar View'}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{language === 'fr' ? 'Aperçu mensuel des séjours — synchronisé en temps réel' : 'Monthly overview of stays — real-time sync'}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* View toggle */}
            <div className="flex items-center bg-muted rounded-xl p-1 gap-1">
              <button
                onClick={() => setActiveView('timeline')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${activeView === 'timeline' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <span className="flex items-center gap-1.5"><Icon name="BarChart2" size={12} />{language === 'fr' ? 'Chronologie' : 'Timeline'}</span>
              </button>
              <button
                onClick={() => setActiveView('calendar')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${activeView === 'calendar' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <span className="flex items-center gap-1.5"><Icon name="CalendarDays" size={12} />{language === 'fr' ? 'Calendrier' : 'Calendar'}</span>
              </button>
            </div>
            {/* Building filter */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {['all', 'F1', 'F2', 'G1', 'G2', 'H', 'L']?.map(b => (
                <button
                  key={b}
                  onClick={() => { setBuildingFilter(b); setSelectedDay(null); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                    buildingFilter === b
                      ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                      : 'bg-card text-muted-foreground border-border hover:border-primary hover:text-foreground'
                  }`}
                >
                  {b === 'all' ? (language === 'fr' ? 'Tous' : 'All') : b}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { icon: 'LogIn', color: 'emerald', label: language === 'fr' ? 'Arrivées ce mois' : 'Check-ins this month', value: monthCheckIns?.length },
            { icon: 'LogOut', color: 'rose', label: language === 'fr' ? 'Départs ce mois' : 'Check-outs this month', value: monthCheckOuts?.length },
            { icon: 'Users', color: 'blue', label: language === 'fr' ? 'Séjours actifs' : 'Active stays', value: activeStays?.length },
            { icon: 'CalendarDays', color: 'amber', label: language === 'fr' ? 'Jours dans le mois' : 'Days in month', value: daysInMonth },
          ]?.map(({ icon, color, label, value }) => (
            <div key={icon} className="bg-card border border-border rounded-xl p-4 flex items-center gap-3 hover:shadow-sm transition-shadow">
              <div className={`w-10 h-10 rounded-xl bg-${color}-100 flex items-center justify-center`}>
                <Icon name={icon} size={18} className={`text-${color}-600`} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-xl font-bold text-foreground">{value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Month navigation */}
        <div className="flex items-center justify-between">
          <button onClick={prevMonth} className="w-9 h-9 flex items-center justify-center rounded-xl border border-border hover:bg-muted hover:border-primary/40 transition-all">
            <Icon name="ChevronLeft" size={18} />
          </button>
          <h2 className="text-lg font-bold text-foreground">{MONTH_NAMES?.[currentMonth]} {currentYear}</h2>
          <button onClick={nextMonth} className="w-9 h-9 flex items-center justify-center rounded-xl border border-border hover:bg-muted hover:border-primary/40 transition-all">
            <Icon name="ChevronRight" size={18} />
          </button>
        </div>

        {/* Timeline View */}
        {activeView === 'timeline' && (
          <TimelineView
            stays={allStays}
            currentYear={currentYear}
            currentMonth={currentMonth}
            language={language}
            buildingFilter={buildingFilter}
          />
        )}

        {/* Calendar View */}
        {activeView === 'calendar' && (
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
              <div className="grid grid-cols-7 border-b border-border">
                {DAY_NAMES?.map(d => (
                  <div key={d} className="py-2.5 text-center text-xs font-semibold text-muted-foreground">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {Array.from({ length: firstDay })?.map((_, i) => (
                  <div key={`empty-${i}`} className="min-h-[90px] border-b border-r border-border bg-muted/10" />
                ))}
                {Array.from({ length: daysInMonth }, (_, i) => i + 1)?.map(day => {
                  const dateStr = toDateStr(currentYear, currentMonth, day);
                  const dayStays = getStaysForDay(dateStr);
                  const isToday = dateStr === todayStr;
                  const isSelected = selectedDay === day;
                  return (
                    <div
                      key={day}
                      onClick={() => setSelectedDay(isSelected ? null : day)}
                      className={`min-h-[90px] border-b border-r border-border p-1.5 cursor-pointer transition-colors relative ${
                        isSelected ? 'bg-primary/8' : 'hover:bg-muted/30'
                      }`}
                    >
                      <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold mb-1 ${
                        isToday ? 'bg-primary text-primary-foreground shadow-sm' : isSelected ? 'bg-primary/20 text-primary' : 'text-foreground'
                      }`}>
                        {day}
                      </div>
                      <div className="space-y-0.5">
                        {dayStays?.slice(0, 3)?.map(s => {
                          const sCfg = STATUS_COLORS?.[s?.status] ?? STATUS_COLORS?.pending;
                          const isStart = s?.checkIn === dateStr;
                          const isEnd = s?.checkOut === dateStr;
                          return (
                            <div
                              key={s?.id}
                              className="flex items-center px-1 py-0.5 rounded text-[10px] font-medium truncate"
                              style={{ background: sCfg?.bg + '22', color: sCfg?.bg, border: `1px solid ${sCfg?.bg}44` }}
                              title={`${s?.residentName} — ${s?.room}`}
                            >
                              {isStart && <Icon name="LogIn" size={8} className="mr-0.5 flex-shrink-0" />}
                              {isEnd && <Icon name="LogOut" size={8} className="mr-0.5 flex-shrink-0" />}
                              <span className="truncate">{s?.residentName?.split(' ')?.[0]} · {s?.room}</span>
                            </div>
                          );
                        })}
                        {dayStays?.length > 3 && (
                          <div className="text-[10px] text-muted-foreground px-1">+{dayStays?.length - 3} {language === 'fr' ? 'autres' : 'more'}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Side panel */}
            <div className="w-full lg:w-72 space-y-4">
              {/* Legend */}
              <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-foreground mb-3">{language === 'fr' ? 'Légende' : 'Legend'}</h3>
                <div className="space-y-2">
                  {LEGEND_STATUSES?.map(cfg => (
                    <div key={cfg?.key} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <div className="w-4 h-3 rounded-sm flex-shrink-0" style={{ background: cfg?.gradient }} />
                      <span>{language === 'fr' ? cfg?.labelFr : cfg?.label}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs font-medium text-muted-foreground mb-2">{language === 'fr' ? 'Bâtiments' : 'Buildings'}</p>
                  <div className="grid grid-cols-3 gap-1.5">
                    {Object.entries(BUILDING_COLORS)?.map(([b, c]) => (
                      <div key={b} className={`flex items-center gap-1.5 px-2 py-1 rounded-lg ${c?.bg}`}>
                        <div className={`w-2 h-2 rounded-full ${c?.dot}`} />
                        <span className={`text-xs font-semibold ${c?.text}`}>{b}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Day detail */}
              {selectedDay && selectedEvents ? (
                <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-foreground">{MONTH_NAMES?.[currentMonth]} {selectedDay}, {currentYear}</h3>
                    <button onClick={() => setSelectedDay(null)} className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-muted transition-colors">
                      <Icon name="X" size={14} />
                    </button>
                  </div>
                  {selectedEvents?.checkIns?.length === 0 && selectedEvents?.checkOuts?.length === 0 && selectedEvents?.active?.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-4">{language === 'fr' ? 'Aucune activité ce jour' : 'No activity on this day'}</p>
                  )}
                  {selectedEvents?.checkIns?.length > 0 && (
                    <div className="mb-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <Icon name="LogIn" size={13} className="text-emerald-600" />
                        <span className="text-xs font-semibold text-emerald-700">{language === 'fr' ? 'Arrivées' : 'Check-ins'} ({selectedEvents?.checkIns?.length})</span>
                      </div>
                      <div className="space-y-2">
                        {selectedEvents?.checkIns?.map(s => (
                          <div key={s?.id} className="bg-muted/50 rounded-xl p-2.5">
                            <p className="text-xs font-semibold text-foreground">{s?.residentName}</p>
                            <p className="text-[11px] text-muted-foreground font-mono">{s?.room}</p>
                            {s?.department && <p className="text-[11px] text-muted-foreground">{s?.department}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedEvents?.active?.length > 0 && (
                    <div className="mb-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <Icon name="Users" size={13} className="text-blue-600" />
                        <span className="text-xs font-semibold text-blue-700">{language === 'fr' ? 'En séjour' : 'In Stay'} ({selectedEvents?.active?.length})</span>
                      </div>
                      <div className="space-y-2">
                        {selectedEvents?.active?.map(s => (
                          <div key={s?.id} className="bg-muted/50 rounded-xl p-2.5">
                            <p className="text-xs font-semibold text-foreground">{s?.residentName}</p>
                            <p className="text-[11px] text-muted-foreground font-mono">{s?.room}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedEvents?.checkOuts?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-1.5 mb-2">
                        <Icon name="LogOut" size={13} className="text-rose-600" />
                        <span className="text-xs font-semibold text-rose-700">{language === 'fr' ? 'Départs' : 'Check-outs'} ({selectedEvents?.checkOuts?.length})</span>
                      </div>
                      <div className="space-y-2">
                        {selectedEvents?.checkOuts?.map(s => (
                          <div key={s?.id} className="bg-muted/50 rounded-xl p-2.5">
                            <p className="text-xs font-semibold text-foreground">{s?.residentName}</p>
                            <p className="text-[11px] text-muted-foreground font-mono">{s?.room}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-card border border-border rounded-2xl p-4 text-center shadow-sm">
                  <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mx-auto mb-2">
                    <Icon name="MousePointerClick" size={18} className="text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Cliquez sur un jour pour voir les détails' : 'Click any day to see details'}</p>
                </div>
              )}

              {/* Live sync */}
              <div className="bg-card border border-border rounded-2xl p-3 flex items-center gap-2 shadow-sm">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Synchronisé en temps réel' : 'Real-time sync active'}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
